import {convertToURLDatetime} from '@domain/shared'
import {usePlantStartOfToday, useUpmPlantTimezone} from '@hconnect/upm'
import {
  parseStartEndDatetimeFromURLParams,
  calculateDatetimeRangeWithinBoundary
} from '@widgets/planning-schedule-chart'
import React, {useMemo, useLayoutEffect} from 'react'

import {LayoutMonthlyPlanning} from '../../components'
import {ProductionPlanningSkeleton} from '../../components/shared/skeletons'
import {SelectedTimeframeProvider, useLongTermPlanBoundary, useSearchParams} from '../../hooks'
import {DialogProvider} from '../../providers'
import type {MomentRange} from '../../types'

export const PageMonthlyProductionPlanning: React.FC = () => {
  const timezoneId = useUpmPlantTimezone()
  // boundary to set limits of scroll back and forward
  const boundary = useLongTermPlanBoundary()

  // we need to infer full timeframe from a selected range from URL
  // TODO abstract it to hook or fn getTimeFrameFromUrlParams
  const [obtainedParams, setSearchParams] = useSearchParams('start', 'end')

  const parsedSelectedRange = useMemo(() => {
    return parseStartEndDatetimeFromURLParams({...obtainedParams, timezoneId})
  }, [obtainedParams, timezoneId])

  const adjustedRange = useMemo(() => {
    if (!parsedSelectedRange) {
      return undefined
    }
    const rangeAdjusteByBoundary = calculateDatetimeRangeWithinBoundary(
      parsedSelectedRange,
      boundary
    )
    const startOfMonthRange = rangeAdjusteByBoundary[0].clone().tz(timezoneId).startOf('month')
    const monthRange: MomentRange = [startOfMonthRange, startOfMonthRange.clone().add(1, 'month')]
    return monthRange
  }, [parsedSelectedRange, boundary, timezoneId])

  const startOfToday = usePlantStartOfToday()

  const defaultTimeframe: MomentRange = [
    startOfToday.clone().startOf('month'),
    startOfToday.clone().startOf('month').add(1, 'month')
  ]

  // get timeframe from url at this point, if not exist, use default timeframe
  const timeframe = adjustedRange ?? defaultTimeframe

  const isParsedRangeCorrect =
    parsedSelectedRange &&
    adjustedRange &&
    parsedSelectedRange[0].isSame(adjustedRange[0]) &&
    parsedSelectedRange[1].isSame(adjustedRange[1])

  useLayoutEffect(() => {
    // if selectedRange is not set, set it to default selected range
    if (!parsedSelectedRange) {
      return setSearchParams({
        start: convertToURLDatetime(timeframe[0].clone().startOf('month'), timezoneId),
        end: convertToURLDatetime(timeframe[0].clone().startOf('month').add(1, 'month'), timezoneId)
      })
    }
  }, [parsedSelectedRange, setSearchParams, timeframe, timezoneId])

  useLayoutEffect(() => {
    // if selectedRange is not correct, set it to adjustedRange
    if (!isParsedRangeCorrect && adjustedRange) {
      return setSearchParams({
        start: convertToURLDatetime(adjustedRange[0], timezoneId),
        end: convertToURLDatetime(adjustedRange[1], timezoneId)
      })
    }
  }, [adjustedRange, isParsedRangeCorrect, setSearchParams, timezoneId])

  if (!isParsedRangeCorrect) {
    return <ProductionPlanningSkeleton height="40vh" />
  }

  return (
    <DialogProvider>
      <SelectedTimeframeProvider defaultTimeframe={timeframe}>
        <LayoutMonthlyPlanning />
      </SelectedTimeframeProvider>
    </DialogProvider>
  )
}
